import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components/macro";
import _ from "lodash";
import { useLocation, useHistory } from "react-router-dom";
import {
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Checkbox,
    TablePagination,
} from "@material-ui/core";
import { Search, Delete, Description, BarChart } from "@material-ui/icons";
import { enqueueSnackbar } from "notistack";
import Loading from "../components/Loading";
import MyRadonInterface from "../../integration/internal/MyRadon/MyRadonInterface";
import moment from "moment";
import { createMuiTheme } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import AnalysisInProgressDialog from "../../components/AnalysisInProgressDialog";
import ReportDeleteConfirmDialog from "../../components/ReportDeleteConfirmDialog";
import {
    FAILURE,
    NO_ELEMENTS_MATCHED,
    SUCCESS,
} from "../../constants/resultCodes";
import * as types from "../../constants";

const theme = createMuiTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    typography: {
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        h6: {
            fontSize: "16px",
        },
    },
});

const Container = styled.div`
  max-width: 1100px;
  margin: auto;
  padding: 24px 16px;
  font-family: "Inter", "Roboto", "Helvetica", "Arial", sans-serif;
`;

const HeaderSection = styled.div`
  margin-bottom: 24px;
`;

const Title = styled(Typography)`
  font-weight: 700 !important;
  font-size: 26px !important;
  color: #1e293b; /* Slate 800 */
  margin-bottom: 6px !important;
`;

const Subtitle = styled(Typography)`
  font-size: 14px !important;
  color: #64748b; /* Slate 500 */
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-grow: 1;
  max-width: 650px;
  min-width: 280px;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  color: #3b82f6; /* Ecosense theme blue search icon */
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 22px;
  border: 1.5px solid #3b82f6; /* Ecosense blue border */
  padding: 0 16px 0 46px;
  outline: none;
  font-size: 14px;
  color: #1e293b;
  background-color: #ffffff;
  transition: box-shadow 0.2s, border-color 0.2s;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-color: #1d4ed8;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  color: ${(props) => (props.disabled ? "#cbd5e1" : "#64748b")};
  font-weight: 500;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    color: ${(props) => (props.disabled ? "#cbd5e1" : "#ef4444")};
    background-color: ${(props) =>
        props.disabled ? "transparent" : "#fef2f2"};
  }
`;

const StyledCard = styled(Card)`
  background: #ffffff;
  border-radius: 12px !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03) !important;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const StyledTable = styled(Table)`
  table-layout: fixed;
  width: 100%;

  @media (max-width: 960px) {
    min-width: 800px;
  }
`;

const StyledTableHead = styled(TableHead)`
  background-color: #ffffff;
`;

const StyledHeaderCell = styled(TableCell)`
  font-weight: 600 !important;
  font-size: 11px !important;
  color: #94a3b8 !important; /* Muted uppercase head cell text */
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 12px !important;
  border-bottom: 1.5px solid #f1f5f9 !important;
`;

const StyledRow = styled(TableRow)`
  cursor: pointer;
  background-color: #ffffff;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f8fafc !important;
  }

  &.Mui-selected,
  &.Mui-selected:hover {
    background-color: #f1f5f9 !important;
  }
`;

const StyledCell = styled(TableCell)`
  font-size: 13px !important;
  color: #334155 !important;
  padding: 16px 12px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SessionNameText = styled.span`
  font-weight: 500;
  color: #1e293b;
`;

const RnAvgWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.color};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  border: 1px solid transparent;

  svg {
    font-size: 14px;
  }

  ${(props) =>
        props.type === "Analysis" &&
        css`
      background-color: #e0f2fe;
      color: #0284c7;
      border-color: #bae6fd;
    `}

  ${(props) =>
        props.type === "Report" &&
        css`
      background-color: #e2f5e2;
      color: #16a34a;
      border-color: #bbf7d0;
    `}
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;

  ${(props) =>
        props.status === "Completed" &&
        css`
      background-color: #dcfce7;
      color: #16a34a;
    `}

  ${(props) =>
        props.status === "In Progress" &&
        css`
      background-color: #fef3c7;
      color: #b45309;
    `}

  ${(props) =>
        props.status === "Cancelled" &&
        css`
      background-color: #f1f5f9;
      color: #475569;
    `}
`;

const ReportListEcosense = () => {
    const location = useLocation();
    const history = useHistory();
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({
        key: "requested",
        direction: "desc",
    });
    const [open, setOpen] = useState(false);
    const [span, setSpan] = useState(null);
    const [collected, setCollected] = useState(null);
    const [serialNumber, setSerialNumber] = useState(null);
    const [selectedReportId, setSelectedReportId] = useState(null);

    // Redesign state
    const [searchQuery, setSearchQuery] = useState("");
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    let { email, reportId } = location.state || {};
    email = email || localStorage.getItem("0xaccountId");

    const handleClose = () => {
        setOpen(false);
        setSpan(null);
        setCollected(null);
        setSerialNumber(null);
        setSelectedReportId(null);
    };

    const handleCancel = async () => {
        if (!serialNumber || !selectedReportId) {
            enqueueSnackbar("Missing serial number or report ID", {
                variant: "error",
            });
            return;
        }

        try {
            setLoading(true);
            const res = await MyRadonInterface.cancelAnalysis(
                serialNumber,
                selectedReportId
            );
            if (res.resultcode === SUCCESS) {
                enqueueSnackbar("Analysis cancelled successfully", {
                    variant: "success",
                });
                // Refresh the table data after cancellation
                const updatedData = await MyRadonInterface.getMyRadonList(email);
                setTableData(
                    _.orderBy(
                        _.get(updatedData, "data", []),
                        [sortConfig.key],
                        [sortConfig.direction]
                    )
                );
            } else {
                throw new Error("Cancellation failed");
            }
        } catch (error) {
            console.error("Error cancelling radon analysis:", error);
            enqueueSnackbar("Error cancelling radon analysis", {
                variant: "eNRPP-Listed Devicerror",
            });
        } finally {
            setLoading(false);
            handleClose();
        }
    };

    useEffect(() => {
        const loadReportList = async () => {
            try {
                setLoading(true);
                // 모의(Mock) 데이터 정의 - 리스트 3개
                const mockList = [
                    {
                        rpt_id: "RPT-252954",
                        device_name: "Basement",
                        requested: 1760012400, // 2025-10-08
                        processed: "Y",
                        sn: "HI01EA000021",
                        span: "2184", // 91 days
                        radonAvg: 4.2,
                        type: "Report"
                    },
                    {
                        rpt_id: "RPT-103829",
                        device_name: "Living Room",
                        requested: 1758630000, // 2025-09-22
                        processed: "Y",
                        sn: "HI01EA000022",
                        span: "72", // 3 days
                        radonAvg: 1.8,
                        type: "Analysis"
                    },
                    {
                        rpt_id: "RPT-882736",
                        device_name: "Master Bedroom",
                        requested: 1755100000, // 2025-08-12
                        processed: "Y",
                        sn: "HI01EA000023",
                        span: "1008", // 42 days
                        radonAvg: 3.1,
                        type: "Report"
                    }
                ];

                setTableData(_.orderBy(mockList, ["requested"], ["desc"]));
            } catch (error) {
                console.error("Error setting mock report list:", error);
            } finally {
                setLoading(false);
            }
        };
        loadReportList();
    }, [email]);

    const handleSort = (key) => {
        const direction =
            sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ key, direction });
        setTableData((prevData) =>
            _.orderBy(prevData, [(row) => getSortValue(row, key)], [direction])
        );
    };

    const handleRowClick = (row) => {
        const { rpt_id, processed, sn, span, collected } = row;
        if (processed === "Y") {
            const typeVal = getReportType(row);
            if (typeVal === "Analysis") {
                history.push("/report2", { email, reportId: rpt_id });
            } else {
                history.push("/radon", { email, reportId: rpt_id });
            }
        } else if (processed === "N") {
            setSpan(span);
            setCollected(collected);
            setSerialNumber(sn);
            setSelectedReportId(rpt_id);
            setOpen(true);
        }
    };

    // Helper functions for displaying columns
    const getRnAvg = (row) => {
        if (row.radonAvg !== undefined && row.radonAvg !== null)
            return parseFloat(row.radonAvg);
        if (row.radon_avg !== undefined && row.radon_avg !== null)
            return parseFloat(row.radon_avg);
        if (
            row.analysis_radon &&
            row.analysis_radon.avg !== undefined &&
            row.analysis_radon.avg !== null
        ) {
            const avgBq = row.analysis_radon.avg;
            return parseFloat((avgBq * 0.027).toFixed(1));
        }
        return null;
    };

    const getDotColor = (avg) => {
        if (avg === null || avg === undefined || isNaN(avg)) return "#e0e0e0";
        if (avg < 2.7) return "#4caf50";
        if (avg <= 3.9) return "#ff9800";
        return "#f44336";
    };

    const getReportType = (row) => {
        if (row.type) {
            const t = row.type.toLowerCase();
            if (t.includes("report")) return "Report";
            if (t.includes("analysis")) return "Analysis";
        }
        if (row.report_type) {
            const t = row.report_type.toLowerCase();
            if (t.includes("report")) return "Report";
            if (t.includes("analysis")) return "Analysis";
        }
        if (row.certified || row.is_certified || row.certified === "Y") {
            return "Report";
        }
        // Fallback: make it deterministic based on ID so there's variety
        const hash =
            String(row.rpt_id).charCodeAt(String(row.rpt_id).length - 1) || 0;
        return hash % 2 === 0 ? "Report" : "Analysis";
    };

    const getDurationStr = (span) => {
        if (!span) return "--";
        const hours = parseInt(span, 10);
        if (isNaN(hours)) return span;
        if (hours === 72) return "72hr";
        if (hours % 24 === 0) {
            return `${hours / 24}d`;
        }
        return `${hours}hr`;
    };

    const getSortValue = (row, key) => {
        if (key === "device_name") return row.device_name || "";
        if (key === "requested") return row.requested || 0;
        if (key === "processed") return row.processed || "";
        if (key === "rnAvg") return getRnAvg(row) ?? -1;
        if (key === "type") return getReportType(row);
        if (key === "duration") return row.span || 0;
        return "";
    };

    // Selection handlers
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = filteredData.map((n) => n.rpt_id);
            setSelected(newSelecteds);
        } else {
            setSelected([]);
        }
    };

    const handleSelectOne = (id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }

        setSelected(newSelected);
    };

    const handleDeleteSelected = () => {
        if (selected.length === 0) return;
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = () => {
        setTableData((prev) =>
            prev.filter((row) => !selected.includes(row.rpt_id))
        );
        enqueueSnackbar(`Successfully deleted ${selected.length} report(s).`, {
            variant: "success",
        });
        setSelected([]);
        setOpenDeleteDialog(false);
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Filter query reset page
    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    const filteredData = tableData.filter((row) => {
        const sName = (row.device_name || "").toLowerCase();
        const type = getReportType(row).toLowerCase();
        const query = searchQuery.toLowerCase();
        return sName.includes(query) || type.includes(query);
    });

    const paginatedData = filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );
    const allSelectedOnPage =
        filteredData.length > 0 &&
        filteredData.every((n) => selected.includes(n.rpt_id));
    const someSelectedOnPage =
        filteredData.some((n) => selected.includes(n.rpt_id)) && !allSelectedOnPage;

    if (loading) return <Loading />;

    return (
        <Container>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <AnalysisInProgressDialog
                    handleClose={handleClose}
                    handleCancel={handleCancel}
                    reportId={selectedReportId}
                    span={span}
                    collected={collected}
                />
            </Dialog>

            <ReportDeleteConfirmDialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                onConfirm={handleConfirmDelete}
                selectedCount={selected.length}
            />

            <HeaderSection>
                <Title variant="h4">My Reports</Title>
                <Subtitle variant="subtitle1">
                    Analysis and Radon Reports from your EcoQube devices.
                </Subtitle>
            </HeaderSection>

            <ActionBar>
                <SearchWrapper>
                    <SearchIconWrapper>
                        <Search style={{ fontSize: 20 }} />
                    </SearchIconWrapper>
                    <SearchInput
                        type="text"
                        placeholder="Search by session or type"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </SearchWrapper>

                <DeleteButton
                    disabled={selected.length === 0}
                    onClick={handleDeleteSelected}
                >
                    <Delete style={{ fontSize: 18 }} />
                    Delete
                </DeleteButton>
            </ActionBar>

            {tableData.length > 0 ? (
                <StyledCard>
                    <CardContent style={{ padding: 0 }}>
                        <TableContainer>
                            <StyledTable>
                                <StyledTableHead>
                                    <TableRow>
                                        <StyledHeaderCell style={{ width: "6%" }}>
                                            <Checkbox
                                                indeterminate={someSelectedOnPage}
                                                checked={allSelectedOnPage}
                                                onChange={handleSelectAll}
                                                color="primary"
                                            />
                                        </StyledHeaderCell>
                                        <StyledHeaderCell style={{ width: "22%" }}>
                                            <TableSortLabel
                                                active={sortConfig.key === "device_name"}
                                                direction={sortConfig.direction}
                                                onClick={() => handleSort("device_name")}
                                            >
                                                Session Name
                                            </TableSortLabel>
                                        </StyledHeaderCell>
                                        <StyledHeaderCell style={{ width: "16%" }} align="center">
                                            <TableSortLabel
                                                active={sortConfig.key === "rnAvg"}
                                                direction={sortConfig.direction}
                                                onClick={() => handleSort("rnAvg")}
                                            >
                                                RN AVG
                                            </TableSortLabel>
                                        </StyledHeaderCell>
                                        <StyledHeaderCell style={{ width: "16%" }} align="center">
                                            <TableSortLabel
                                                active={sortConfig.key === "type"}
                                                direction={sortConfig.direction}
                                                onClick={() => handleSort("type")}
                                            >
                                                Type
                                            </TableSortLabel>
                                        </StyledHeaderCell>
                                        <StyledHeaderCell style={{ width: "14%" }} align="center">
                                            <TableSortLabel
                                                active={sortConfig.key === "duration"}
                                                direction={sortConfig.direction}
                                                onClick={() => handleSort("duration")}
                                            >
                                                Duration
                                            </TableSortLabel>
                                        </StyledHeaderCell>
                                        <StyledHeaderCell style={{ width: "14%" }} align="center">
                                            <TableSortLabel
                                                active={sortConfig.key === "processed"}
                                                direction={sortConfig.direction}
                                                onClick={() => handleSort("processed")}
                                            >
                                                Status
                                            </TableSortLabel>
                                        </StyledHeaderCell>
                                        <StyledHeaderCell style={{ width: "12%" }} align="center">
                                            <TableSortLabel
                                                active={sortConfig.key === "requested"}
                                                direction={sortConfig.direction}
                                                onClick={() => handleSort("requested")}
                                            >
                                                Requested
                                            </TableSortLabel>
                                        </StyledHeaderCell>
                                    </TableRow>
                                </StyledTableHead>
                                <TableBody>
                                    {paginatedData.length > 0 ? (
                                        paginatedData.map((row) => {
                                            const {
                                                rpt_id,
                                                device_name,
                                                requested,
                                                processed,
                                                sn,
                                                span,
                                                collected,
                                            } = row;
                                            const isSelected = selected.includes(rpt_id);
                                            const avgValue = getRnAvg(row);
                                            const typeVal = getReportType(row);
                                            const durationStr = getDurationStr(span);

                                            return (
                                                <StyledRow
                                                    key={rpt_id}
                                                    hover
                                                    onClick={() => handleRowClick(row)}
                                                    selected={isSelected}
                                                >
                                                    <StyledCell onClick={(e) => e.stopPropagation()}>
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onChange={() => handleSelectOne(rpt_id)}
                                                            color="primary"
                                                        />
                                                    </StyledCell>
                                                    <StyledCell>
                                                        <SessionNameText>
                                                            {device_name || "--"}
                                                        </SessionNameText>
                                                    </StyledCell>
                                                    <StyledCell align="center">
                                                        <RnAvgWrapper style={{ justifyContent: "center" }}>
                                                            <StatusDot color={getDotColor(avgValue)} />
                                                            <span>
                                                                {avgValue !== null ? avgValue.toFixed(1) : "--"}
                                                            </span>
                                                        </RnAvgWrapper>
                                                    </StyledCell>
                                                    <StyledCell align="center">
                                                        <Badge type={typeVal}>
                                                            {typeVal === "Analysis" ? (
                                                                <BarChart />
                                                            ) : (
                                                                <Description />
                                                            )}
                                                            {typeVal}
                                                        </Badge>
                                                    </StyledCell>
                                                    <StyledCell align="center">{durationStr}</StyledCell>
                                                    <StyledCell align="center">
                                                        <StatusBadge
                                                            status={
                                                                processed === "Y"
                                                                    ? "Completed"
                                                                    : processed === "N"
                                                                        ? "In Progress"
                                                                        : "Cancelled"
                                                            }
                                                        >
                                                            {processed === "Y"
                                                                ? "Completed"
                                                                : processed === "N"
                                                                    ? "In Progress"
                                                                    : "Cancelled"}
                                                        </StatusBadge>
                                                    </StyledCell>
                                                    <StyledCell align="center">
                                                        {moment.unix(requested).format("MM/DD/YY")}
                                                    </StyledCell>
                                                </StyledRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={7}
                                                align="center"
                                                style={{ padding: "40px 0", color: "#64748b" }}
                                            >
                                                No matching reports found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </StyledTable>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </CardContent>
                </StyledCard>
            ) : (
                <Card>
                    <CardContent>
                        <Typography
                            variant="h6"
                            style={{
                                textAlign: "center",
                                color: "#808080",
                                fontSize: "13px",
                            }}
                        >
                            {email
                                ? "There are no completed reports generated from this email account."
                                : "There's no passed email. Please try again."}
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Container>
    );
};

export default ReportListEcosense;
